import { memo, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  type SocialPlatform,
  PLATFORM_OPTIONS,
} from '../../features/posts/model/platforms'
import { Search, Filter, Calendar, ChevronDown } from 'lucide-react'

type DateRangeOption = 'all' | '1d' | '7d' | '30d' | 'custom'

type SearchFilterBarProps = {
  onSearch?: (query: string) => void
  onPlatformChange?: (platform: SocialPlatform | 'all') => void
  onDateRangeChange?: (start?: string, end?: string) => void
  initialPlatform?: SocialPlatform | 'all'
  isSticky?: boolean
}

export const SearchFilterBar = memo(function SearchFilterBar({
  onSearch,
  onPlatformChange,
  onDateRangeChange,
  initialPlatform = 'all',
  isSticky = false,
}: SearchFilterBarProps) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [platform, setPlatform] = useState<SocialPlatform | 'all'>(initialPlatform)
  const [rangeOption, setRangeOption] = useState<DateRangeOption>('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const handleSearch = (value: string) => {
    setSearchQuery(value)
    onSearch?.(value)
  }

  const handlePlatformChange = (value: SocialPlatform | 'all') => {
    setPlatform(value)
    onPlatformChange?.(value)
  }

  const updateRange = useCallback(
    (option: DateRangeOption, start?: string, end?: string) => {
      setRangeOption(option)

      let finalStart = start
      let finalEnd = end

      if (option !== 'custom') {
        finalEnd = undefined

        if (option === '1d') {
          const d = new Date()
          d.setDate(d.getDate() - 1)
          finalStart = d.toISOString()
        } else if (option === '7d') {
          const d = new Date()
          d.setDate(d.getDate() - 7)
          finalStart = d.toISOString()
        } else if (option === '30d') {
          const d = new Date()
          d.setMonth(d.getMonth() - 1)
          finalStart = d.toISOString()
        } else {
          finalStart = undefined
        }
      }

      onDateRangeChange?.(finalStart, finalEnd)
    },
    [onDateRangeChange],
  )

  const rangeOptions = useMemo(
    () => [
      { label: t('filters.allTime'), value: 'all' },
      { label: t('filters.last1Day'), value: '1d' },
      { label: t('filters.last7Days'), value: '7d' },
      { label: t('filters.lastMonth'), value: '30d' },
      { label: t('filters.custom'), value: 'custom' },
    ],
    [t],
  )

  return (
    <div
      className={cn(
        'border-b border-border bg-card/50 backdrop-blur-md px-6 py-4',
        isSticky ? 'relative lg:sticky lg:top-0 lg:z-20 lg:shadow-sm' : 'relative',
      )}
    >
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search Input */}
          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground dark:text-neutral-500" />
            <input
              type="search"
              placeholder={t('topbar.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="h-11 w-full rounded-full border border-border bg-muted/30 pl-10 pr-4 text-[14px] text-foreground outline-none backdrop-blur-md transition-all placeholder:text-muted-foreground/60 focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
            />
          </div>

          {/* Platform Select */}
          <div className="relative">
            <Filter className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <select
              value={platform}
              onChange={(e) =>
                handlePlatformChange(e.target.value as SocialPlatform | 'all')
              }
              className="h-11 min-w-[160px] appearance-none cursor-pointer rounded-lg border border-border bg-muted/30 pl-9 pr-8 text-sm text-foreground outline-none transition-all hover:bg-muted/50 focus:border-primary"
            >
              <option value="all">{t('filters.allPlatforms')}</option>
              {PLATFORM_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {t(`platforms.${p}`)}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          </div>

          {/* Date Range Selector */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-muted/30 p-1">
            {rangeOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => updateRange(opt.value as DateRangeOption)}
                className={cn(
                  'relative min-h-[44px] cursor-pointer rounded-md px-3 text-[13px] font-medium transition-all duration-200',
                  rangeOption === opt.value
                    ? 'bg-selected text-selected-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-white/5 hover:text-foreground',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Date Inputs */}
        {rangeOption === 'custom' && (
          <div className="flex animate-in fade-in slide-in-from-top-2 items-center gap-3 border-t border-border/50 pt-4 duration-300">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value)
                  onDateRangeChange?.(e.target.value || undefined, endDate || undefined)
                }}
                className="h-9 rounded-md border border-border bg-muted/30 px-3 text-[13px] text-foreground outline-none transition-all focus:border-primary"
              />
            </div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {t('filters.to')}
            </span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value)
                onDateRangeChange?.(startDate || undefined, e.target.value || undefined)
              }}
              className="h-9 rounded-md border border-border bg-muted/30 px-3 text-[13px] text-foreground outline-none transition-all focus:border-primary"
            />
          </div>
        )}
      </div>
    </div>
  )
})
